import {gql} from '@apollo/client'
import {Schema} from '../create/schema'
import {BASIC_BASE_DATE_TYPE_VALUES} from 'consts/alertCustom'

interface MutationAlertType {
  id: string
  color: number
  name: string
  description: string
  baseDateType: string
  alertTargets: string[] | null
  offset: number
  days: number
  dateUnit: string
  active: boolean
  leaveOfAbsenceReason: string | null
  customEmployeeFieldId: string | null
  adminMailActive: boolean
  adminMailSubject: string
  adminMailBody: string
  employeeMailActive: boolean
  employeeMailSubject: string
  employeeMailBody: string
  destroyEmployeeAlerts: boolean
  selectedOfficeIds: string[]
  selectedAllOffices: boolean
}

export const convertFormDataToMutationAlertType = (
  alertTypeInput: Schema,
  id: string,
  isDestroy = false
): MutationAlertType => {
  const days = alertTypeInput.isBefore === 'Before' ? -Number(alertTypeInput.days) : Number(alertTypeInput.days)
  const baseDateType = BASIC_BASE_DATE_TYPE_VALUES.includes(alertTypeInput.baseDateType.value)
    ? alertTypeInput.baseDateType.value
    : 'custom_value'
  const alertTargets =
    baseDateType === 'birthday' || baseDateType === 'age'
      ? alertTypeInput.alertTargets.map((option) => option.value)
      : null
  const leaveOfAbsenceReason =
    baseDateType === 'absence_start_day' || baseDateType === 'absence_end_day' ? alertTypeInput.reasonType?.value : null
  const customEmployeeFieldId = baseDateType === 'custom_value' ? alertTypeInput.baseDateType.value : null
  const offset = baseDateType === 'age' || baseDateType === 'service_years' ? Number(alertTypeInput.offset) : 0

  const mutationAlertType: MutationAlertType = {
    id: id,
    color: Number(alertTypeInput.color),
    name: alertTypeInput.name,
    description: alertTypeInput.description,
    baseDateType: baseDateType,
    alertTargets: alertTargets,
    offset: offset ?? 0,
    days: days,
    dateUnit: alertTypeInput.dateUnit,
    active: alertTypeInput.active,
    leaveOfAbsenceReason: leaveOfAbsenceReason ?? null,
    customEmployeeFieldId: customEmployeeFieldId,
    adminMailActive: alertTypeInput.adminMailActive,
    adminMailSubject: alertTypeInput.adminMailSubject ?? '',
    adminMailBody: alertTypeInput.adminMailBody ?? '',
    employeeMailActive: alertTypeInput.employeeMailActive,
    employeeMailSubject: alertTypeInput.employeeMailSubject ?? '',
    employeeMailBody: alertTypeInput.employeeMailBody ?? '',
    destroyEmployeeAlerts: isDestroy,
    selectedOfficeIds: alertTypeInput.selectedOfficeIds ?? [],
    selectedAllOffices: alertTypeInput.selectedAllOffices,
  }
  return mutationAlertType
}

export const isChangeNotificationDate = (defaultValues: Schema | undefined, formValues: Schema) => {
  const isBaseDateTypeEqual = defaultValues?.baseDateType.value === formValues.baseDateType.value
  const isOffsetEqual = defaultValues?.offset === formValues.offset
  const isDateUnitEqual = defaultValues?.dateUnit === formValues.dateUnit
  const isDaysEqual = defaultValues?.days === formValues.days
  const isBeforeEqual = defaultValues?.isBefore === formValues.isBefore

  const defaultAlertTargetValues = defaultValues?.alertTargets?.map((t) => t.value) ?? []
  const formAlertTargetValues = formValues?.alertTargets?.map((t) => t.value) ?? []
  const isAlertTargetsEqual =
    defaultAlertTargetValues.length === formAlertTargetValues.length &&
    defaultAlertTargetValues.every((v) => formAlertTargetValues.includes(v)) &&
    formAlertTargetValues.every((v) => defaultAlertTargetValues.includes(v))

  const isReasonTypeEqual =
    !['absence_start_day', 'absence_end_day'].includes(defaultValues?.baseDateType?.value ?? '') ||
    !['absence_start_day', 'absence_end_day'].includes(formValues.baseDateType.value) ||
    defaultValues?.reasonType?.value === formValues.reasonType?.value

  return !(
    isBaseDateTypeEqual &&
    isOffsetEqual &&
    isDateUnitEqual &&
    isDaysEqual &&
    isBeforeEqual &&
    isReasonTypeEqual &&
    isAlertTargetsEqual
  )
}

export const UPDATE_ALERT_TYPE = gql`
  mutation ($input: UpdateAlertInput!) {
    updateAlertType(input: $input) {
      alertType {
        id
        color
        name
        description
        clientId
        alertTypeNotifications {
          destination
          subject
          body
          active
        }
      }
    }
  }
`
